/**
 * A saved report's filters expressed as `ReportParameter`s, so they render with the studio's own
 * compact [[ReportParameterField]]. Resolves each filter's `hierarchy_id` from
 * `/pnl-dimensions/{dim}/hierarchies` and hands the spec's members over as the default; `scope_root`
 * limits the picker to the subtree the report was built on.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReportFilter } from 'src/components/star-grid/api/reportEngineTypes';
import type { ReportParameter } from 'src/components/star-grid/reportDefinition.types';
import { listPnlDimensionHierarchies } from 'src/components/star-grid/services/pnlDimensionService';
import { hierarchyPairId } from 'src/components/star-grid/services/filterHierarchyService';
import { screenChoices } from 'src/components/star-grid/utils/screenOptions';

/** Same normalisation the studio's binding match uses, so `CostCenter` meets `Cost Center`. */
const normalize = (name: string): string =>
  String(name ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

export interface UseReportFilterParametersResult {
  readonly parameters: readonly ReportParameter[];
  readonly loading: boolean;
}

/**
 * What the catalog answered for one filter: its ALIAS, and nothing else.
 *
 * The tree is NOT keyed on the catalog's id — it is addressed by (dimension, hierarchy) pair, because
 * only 8 of the 22 pairs the deck filters on are registered with an id.
 *
 * Keyed by dimension AND hierarchy rather than by dimension alone: a deck screen filters the same
 * dimension through several hierarchies (`FPA Deck Line Item` via `ChannelPL`, via `PL Line`, …), so a
 * dimension-keyed map let the last one win and rendered several chips with one identical label.
 */
interface ResolvedFilter {
  readonly label?: string;
}

/**
 * A readable name for a filter the catalog has no alias for.
 *
 * Saved specs state their filters in the FACT-KEY form (`fpa_deck_country`, `channelpl`), so a chip
 * rendered straight from the spec read `fpa_deck_country` — the raw column name, which is what the
 * bar was showing instead of Prophecy's `Country`. Handles both that form and the display form, and
 * drops the `FPA Deck` prefix either way: it repeats on most chips and pushes the part that actually
 * distinguishes them out of the width the bar gives a label.
 */
export const prettyFilterName = (raw: string): string => {
  const words = raw
    .replace(/[_]+/g, ' ')
    .replace(/^\}APQ_?/, '')
    .trim()
    .replace(/^FPA Deck\s*/i, '')
    .trim();
  if (!words) return '';
  return words
    .split(/\s+/)
    .map((word) => (/[a-z]/.test(word) && /[A-Z]/.test(word) ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');
};

/** The map key. Mirrors the cache key, so a resolved entry is always findable. */
const filterKey = (filter: ReportFilter): string => `${filter.dimension}::${filter.hierarchy ?? ''}`;

/**
 * The chip's label. The catalog's ALIAS first — `filter.dimension` is a physical name
 * (`FPA Deck Line Item`, `}APQ_Currency Selection`) and is what Prophecy's bar never shows.
 *
 * With no alias, the hierarchy names the filter better than the dimension does: it is what
 * distinguishes two filters sharing a dimension, and it is the only label-ish field a deck filter
 * entry carries. The `FPA Deck ` prefix is dropped because it repeats on every chip and pushes the
 * distinguishing part out of the 150px the bar gives a label.
 */
const chipLabel = (filter: ReportFilter, resolved: ResolvedFilter | undefined): string => {
  const alias = resolved?.label?.trim();
  if (alias) return alias;
  return prettyFilterName((filter.hierarchy ?? '').trim() || filter.dimension) || filter.dimension;
};

const NO_TREES: Readonly<Record<string, string>> = {};

export function useReportFilterParameters(
  filters: readonly ReportFilter[],
  trees: Readonly<Record<string, string>> = NO_TREES
): UseReportFilterParametersResult {
  const [resolved, setResolved] = useState<Readonly<Record<string, ResolvedFilter>>>({});
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | undefined>(undefined);

  // Keyed on the dimension list rather than the array identity: the spec object is replaced on every
  // query, and re-fetching the hierarchies each time would re-mount the pickers mid-interaction.
  const key = filters.map(filterKey).join('|');

  useEffect(() => {
    if (filters.length === 0) return undefined;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    const dimensions = [...new Set(filters.map((filter) => filter.dimension))];
    Promise.all(
      dimensions.map(async (dimension) => {
        try {
          return [dimension, await listPnlDimensionHierarchies(dimension, controller.signal)] as const;
        } catch {
          // One dimension failing must not cost the others their pickers; that filter simply falls
          // back to the flat control instead of the tree.
          return [dimension, []] as const;
        }
      })
    )
      .then((loaded) => {
        if (controller.signal.aborted) return;
        const found: Record<string, ResolvedFilter> = {};
        for (const filter of filters) {
          const wanted = normalize(filter.hierarchy ?? filter.dimension);
          const candidates = loaded.find(([dimension]) => dimension === filter.dimension)?.[1] ?? [];
          // The EXACT hierarchy only: a sibling hierarchy of the same dimension has a different
          // label, and naming the chip after it would state a filter the report does not apply.
          const exact = candidates.find((entry) => normalize(entry.hierarchy ?? entry.cdp_hierarchy ?? '') === wanted);
          found[filterKey(filter)] = { label: exact?.label ?? undefined };
        }
        setResolved(found);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [key]);

  const parameters = useMemo(
    () =>
      filters.map<ReportParameter>((filter) => {
        const members = filter.members ?? [];
        const entry = resolved[filterKey(filter)];
        const screenOptions = screenChoices(filter.options);
        const pair = hierarchyPairId(filter.dimension, filter.hierarchy ?? filter.dimension);
        // A flat screen list wins: exactly its choices, in its order. A nesting one is a tree in
        // Prophecy, so it takes the tree below, rooted at the screen's first (top) option.
        if (screenOptions.length > 0 && !filter.tree)
          return {
            key: pair,
            label: chipLabel(filter, entry),
            role: 'fact_filter',
            required: false,
            multiple: members.length > 1,
            hierarchy_id: null,
            options: screenOptions,
            default: [...members],
            scope_root: null
          };
        return {
          // The (dimension, hierarchy) pair is the key, because the override matches on it: Channel and
          // Allocation share the line-item dimension, and keying on it alone let one chip clear the other.
          key: hierarchyPairId(filter.dimension, filter.hierarchy ?? filter.dimension),
          label: chipLabel(filter, entry),
          role: 'fact_filter',
          required: false,
          // Single-select on the deck's own screens, where each filter states exactly one member
          // (`Version` = Live, `Channel` = Total). A report that genuinely pinned several keeps the
          // multi-select control, because narrowing it to one would silently drop scope the report
          // was saved with.
          multiple: members.length > 1,
          // The (dimension, hierarchy) PAIR, never the catalog's id, and ALWAYS set. Only 8 of the 22
          // pairs the deck filters on are registered with an id, so keying the tree on one left most
          // chips as a flat multiselect with no hierarchy panel and no member-subset modal. The pair
          // is resolved by `loadFilterHierarchy`, which every report filter is rendered with.
          hierarchy_id: trees[pair] ?? pair,
          // ALWAYS set, and this is load-bearing rather than cosmetic. `useReportParameter` derives
          // `usesDefault` as `!hierarchy_id && !options`, and `ReportParameterFilter` renders a
          // static, unclickable `<span>` whenever that is true. Since `hierarchy_id` is resolved
          // asynchronously — and stays null for good when a dimension has no rollup entry or the
          // lookup fails — leaving `options` unset made every filter dead text on first paint and
          // permanently dead on any dimension the lookup could not answer for. Seeding them from the
          // report's own members means the chip is an interactive control in every state: flat while
          // the hierarchy loads or if it never arrives, the tree once it does. Degrade to flat, never
          // to static.
          options: members.map((member) => ({ value: member, label: member })),
          default: [...members],
          scope_root: (filter.tree ? screenOptions[0]?.value : undefined) ?? members[0] ?? null
        };
      }),
    [filters, resolved, trees]
  );

  return { parameters, loading };
}
